import React, { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { LscLogo } from '../components/LscLogo';
import LscFooter from '../components/LscFooter';
import {
  COMING_SOON_EYEBROW,
  COMING_SOON_HEADLINE,
  COMING_SOON_STUDIO_CTA,
  COMING_SOON_SUBMIT,
  COMING_SOON_SUCCESS,
  COMING_SOON_WAITLIST_DETAIL,
  COMING_SOON_WAITLIST_HEADING,
  LANDING_HERO_PRIVACY,
  LANDING_HERO_PRIVACY_LINK,
  LANDING_SUBHEADLINE,
} from '../landing/landingCopy';
import { landingFeaturedReplayAssetUrl, LANDING_FEATURED_REPLAY_ALT } from '../landing/landingMarketingAssets';
import { submitLscWaitlist, validateLscWaitlistForm } from '../waitlist';
import { legalPageUrl, isAbsoluteLegalUrl } from '../../legal/legalLinks';

const LscComingSoonPage: React.FC = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    const clientError = validateLscWaitlistForm({ firstName, lastName, email });
    if (clientError) {
      setError(clientError);
      return;
    }
    setSubmitting(true);
    try {
      await submitLscWaitlist({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        source: 'coming_soon',
      });
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not join the waitlist. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-secondary-900 text-white flex flex-col"
      data-testid="lsc-coming-soon"
    >
      <main className="flex-1">
        <div className="space-y-16 md:space-y-20 py-10 md:py-16 px-4 max-w-5xl mx-auto">
          <section className="relative text-center space-y-8" data-testid="lsc-coming-soon-hero">
            <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden" aria-hidden>
              <div className="absolute top-0 left-1/2 h-72 w-[min(100%,42rem)] -translate-x-1/2 rounded-full bg-primary-600/20 blur-3xl" />
              <div className="absolute top-24 left-1/2 h-px w-[min(90%,36rem)] -translate-x-1/2 bg-gradient-to-r from-transparent via-primary-500/50 to-transparent" />
            </div>
            <p className="text-sm font-semibold tracking-[0.28em] uppercase text-primary-300">
              {COMING_SOON_EYEBROW}
            </p>
            <LscLogo variant="hero" className="mx-auto items-center" />
            <h1
              className="text-5xl md:text-7xl lg:text-8xl font-black text-white leading-none tracking-tight"
              data-testid="lsc-coming-soon-headline"
            >
              {COMING_SOON_HEADLINE}
            </h1>
            <p className="text-lg md:text-xl text-secondary-300 max-w-3xl mx-auto leading-relaxed">
              {LANDING_SUBHEADLINE}
            </p>
            <p className="text-sm text-secondary-400 max-w-2xl mx-auto">
              {LANDING_HERO_PRIVACY}{' '}
              {isAbsoluteLegalUrl(legalPageUrl('privacy')) ? (
                <a
                  href={legalPageUrl('privacy')}
                  className="text-primary-300 hover:text-primary-200 underline underline-offset-2"
                >
                  {LANDING_HERO_PRIVACY_LINK}
                </a>
              ) : (
                <Link
                  to={legalPageUrl('privacy')}
                  className="text-primary-300 hover:text-primary-200 underline underline-offset-2"
                >
                  {LANDING_HERO_PRIVACY_LINK}
                </Link>
              )}
              .
            </p>
            <Link
              to="/studio"
              className="inline-flex items-center justify-center rounded-full bg-primary-600 px-6 py-3 text-sm font-semibold text-white hover:bg-primary-500"
              data-testid="lsc-coming-soon-studio-cta"
            >
              {COMING_SOON_STUDIO_CTA}
            </Link>
          </section>

          <section
            className="rounded-2xl border border-secondary-700 bg-secondary-800/60 p-6 md:p-10 space-y-6"
            data-testid="lsc-waitlist-form"
          >
            <div className="space-y-2 text-center">
              <h2 className="text-2xl md:text-3xl font-bold">{COMING_SOON_WAITLIST_HEADING}</h2>
              <p className="text-secondary-300">{COMING_SOON_WAITLIST_DETAIL}</p>
            </div>
            {success ? (
              <p
                className="text-center text-lg text-primary-200"
                role="status"
                data-testid="lsc-waitlist-success"
              >
                {COMING_SOON_SUCCESS}
              </p>
            ) : (
              <form className="grid gap-4 max-w-xl mx-auto" onSubmit={onSubmit} noValidate>
                <label className="grid gap-1 text-left">
                  <span className="text-sm text-secondary-300">First name</span>
                  <input
                    name="first_name"
                    autoComplete="given-name"
                    value={firstName}
                    onChange={(event) => setFirstName(event.target.value)}
                    className="rounded-lg border border-secondary-600 bg-secondary-900 px-3 py-2 text-white"
                    required
                  />
                </label>
                <label className="grid gap-1 text-left">
                  <span className="text-sm text-secondary-300">Last name</span>
                  <input
                    name="last_name"
                    autoComplete="family-name"
                    value={lastName}
                    onChange={(event) => setLastName(event.target.value)}
                    className="rounded-lg border border-secondary-600 bg-secondary-900 px-3 py-2 text-white"
                    required
                  />
                </label>
                <label className="grid gap-1 text-left">
                  <span className="text-sm text-secondary-300">Email</span>
                  <input
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    className="rounded-lg border border-secondary-600 bg-secondary-900 px-3 py-2 text-white"
                    required
                  />
                </label>
                {error ? (
                  <p className="text-sm text-red-300" role="alert" data-testid="lsc-waitlist-error">
                    {error}
                  </p>
                ) : null}
                <button
                  type="submit"
                  className="btn-primary mt-2"
                  disabled={submitting}
                  data-testid="lsc-waitlist-submit"
                >
                  {submitting ? 'Joining…' : COMING_SOON_SUBMIT}
                </button>
              </form>
            )}
          </section>

          <figure className="overflow-hidden rounded-2xl border border-secondary-700">
            <img
              src={landingFeaturedReplayAssetUrl()}
              alt={LANDING_FEATURED_REPLAY_ALT}
              className="w-full h-auto"
            />
          </figure>
        </div>
      </main>
      <LscFooter />
    </div>
  );
};

export default LscComingSoonPage;
